import React from 'react';
import { Loader2, Undo2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { _money } from './utils';

// iter494 — Undo Last Master Push
// Two-step flow: (1) preview the batch, (2) confirm cancel.
// The dialog body scrolls between a pinned header + footer so a
// bulk push with hundreds of rows can still be reviewed comfortably.
export function UndoLastPushDialog({ items, batchAt, onCancel, onConfirm }) {
  const [busy, setBusy] = React.useState(false);
  const total = items.length;
  const bySrc = React.useMemo(() => {
    const acc = { override: 0, uplift: 0, other: 0 };
    for (const i of items) {
      if (i.source === 'budget_uplift_promote') acc.uplift += 1;
      else if (i.source === 'budget_promote' || i.source === 'budget_override_promote') acc.override += 1;
      else acc.other += 1;
    }
    return acc;
  }, [items]);

  const run = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };

  const label = (src) => (
    src === 'budget_uplift_promote' ? 'via Uplift'
      : src === 'budget_override_promote' ? 'Override (bulk)'
      : src === 'budget_promote' ? 'Override' : src
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent
        data-testid="undo-last-push-dialog"
        className="max-w-[95vw] w-[95vw] max-h-[85vh] flex flex-col overflow-hidden"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Undo last master push</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
          <div className="text-xs text-slate-600">
            You&apos;re about to cancel <b>{total}</b> price change{total === 1 ? '' : 's'}
            {batchAt ? ` written on ${new Date(batchAt).toLocaleString()}` : ''}.
            {' '}Cancelled rows stop applying immediately (past-dated) or on their effective date (future-dated), and stay on the site&apos;s history log for audit — nothing is deleted.
          </div>
          <div className="text-[11px] text-slate-500 flex gap-3 flex-wrap">
            <span>Overrides: <b>{bySrc.override}</b></span>
            <span>Uplifts: <b>{bySrc.uplift}</b></span>
            {bySrc.other > 0 && <span>Other: <b>{bySrc.other}</b></span>}
          </div>

          {total === 0 ? (
            <div className="text-xs text-slate-500 italic py-6 text-center">
              No recent master push to undo.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-600 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Site</th>
                    <th className="text-right px-2 py-1.5">Was (master)</th>
                    <th className="text-right px-2 py-1.5">→ Pushed £/job</th>
                    <th className="text-right px-2 py-1.5">↺ Reverts to</th>
                    <th className="text-left px-2 py-1.5">Source</th>
                    <th className="text-left px-2 py-1.5">Effective from</th>
                    <th className="text-left px-2 py-1.5">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // iter494b — Group by client_name for visual clustering.
                    const order = [];
                    const buckets = new Map();
                    for (const i of items) {
                      const key = i.client_name || 'Unassigned';
                      if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
                      buckets.get(key).push(i);
                    }
                    return order.map(client_name => (
                      <React.Fragment key={`undo-grp-${client_name}`}>
                        <tr className="bg-slate-50 border-t">
                          <td colSpan={7} className="px-2 py-1 font-semibold uppercase text-[10px] tracking-wide text-slate-600">
                            {client_name} <span className="text-slate-400 font-normal normal-case">· {buckets.get(client_name).length} row{buckets.get(client_name).length === 1 ? '' : 's'}</span>
                          </td>
                        </tr>
                        {buckets.get(client_name).map(i => (
                          <tr key={i.id} className="border-t" data-testid={`undo-row-${i.id}`}>
                            <td className="px-2 py-1.5 font-medium pl-6">{i.site_name}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-500">{i.master_price != null ? _money(i.master_price) : '—'}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{i.price != null ? _money(i.price) : '—'}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-rose-700">{i.master_price != null ? _money(i.master_price) : '—'}</td>
                            <td className={`px-2 py-1.5 text-[11px] ${i.source === 'budget_uplift_promote' ? 'text-emerald-700' : 'text-slate-600'}`}>
                              {label(i.source)}
                            </td>
                            <td className="px-2 py-1.5 text-slate-500">{i.effective_from || '—'}</td>
                            <td className="px-2 py-1.5 text-slate-500 truncate max-w-xs">{i.notes || '—'}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t bg-background">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="undo-cancel-btn">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={run}
            disabled={busy || total === 0}
            data-testid="undo-confirm-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1" />}
            {`Undo ${total} change${total === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
